import { IProduceJoinDto } from "../../dto";
import {
  AssignDocksDuringProductionEnum,
  BulkProduceTypeEnum,
  DirectionEnum,
  DirectionsServiceEnum,
  EtlBookingTypeEnum,
  ProductionStatusEnum,
  ProductionTypeEnum,
} from "../../enums";
import { TInfoItem } from "../common.models";
import { IRichLogData } from "../rich-log";
import { ICourseScheduleMetricItem } from "./course-schedule-metric.models";

export type { ICourseScheduleMetricItem } from "./course-schedule-metric.models";

export type TCustomProductionFieldName =
  | "productionAction"
  | "productionType"
  | "produceFrom"
  | "produceTo"
  | "shiftIds"
  | "direction"
  | "unitIds"
  | "siteIds"
  | "areaIds"
  | "passengerTypeId"
  | "rideTypeId"
  | "bookingType"
  | "sectors"
  | "assignDocksDuringProduction"
  | "useCustomDirectionsService"
  | "directionsService"
  | "doNotLockBookings";

export interface IProductionForm {
  productionAction: BulkProduceTypeEnum;
  productionType?: ProductionTypeEnum[];
  produceFrom: TDateISO;
  produceTo: TDateISO;
  direction: DirectionEnum;
  shiftIds?: number[];
  unitIds?: number[];
  siteIds?: number[];
  areaIds?: number[];
  passengerTypeId?: number[];
  rideTypeId?: number;
  bookingType?: EtlBookingTypeEnum;
  sectors?: string[];
  assignDocksDuringProduction?: AssignDocksDuringProductionEnum;
  useCustomDirectionsService?: boolean;
  directionsService?: DirectionsServiceEnum;
  doNotLockBookings?: boolean;
}

export const CustomProductionFormFields = Object.freeze({
  productionAction: "productionAction",
  productionType: "productionType",
  produceFrom: "produceFrom",
  produceTo: "produceTo",
  direction: "direction",
  shiftIds: "shiftIds",
  unitIds: "unitIds",
  siteIds: "siteIds",
  areaIds: "areaIds",
  passengerTypeId: "passengerTypeId",
  rideTypeId: "rideTypeId",
  bookingType: "bookingType",
  sectors: "sectors",
  assignDocksDuringProduction: "assignDocksDuringProduction",
  useCustomDirectionsService: "useCustomDirectionsService",
  directionsService: "directionsService",
  doNotLockBookings: "doNotLockBookings",
}) satisfies Record<string, TCustomProductionFieldName>;

export interface IBookingsProductionDrawerConfigurationForm extends Omit<
  IProductionForm,
  "direction"
> {
  direction: DirectionEnum | null;
}

export const resolveProduceDirectionsServicePayload = (
  directionsService?: DirectionsServiceEnum | null,
): Pick<
  IProductionForm,
  "useCustomDirectionsService" | "directionsService"
> => ({
  useCustomDirectionsService: !!directionsService,
  directionsService: directionsService || undefined,
});

export interface IProductionSummaryItem {
  label: string;
  value: string;
}

export interface IProduceInitResultData {
  productionGroupId: number;
  productionIds: number[];
  states: IProductionPollState[];
  chains: IProductionChainStep[];
}

export interface IProductionChainStep {
  productionId: number;
  data: IProduceJoinDto;
  chainLevel: number;
  chainName: string;
}

export interface IProduceCheckStatusPayload {
  productionId: number;
  progress?: number;
  isFinished?: boolean;
}

export interface IProduceFetchLogsPayload {
  productionId: number;
}
export interface IFetchProduceLogsResultData {
  productionId: number;
  logsData?: IRichLogData;
  logsRecordsCompressed?: string;
}

export interface IProductionCheckResultData {
  productionId: number;
  state?: IProductionPollState;
}

export interface IProduceAbortPayload {
  productionId: number;
}

export interface IProduceAbortResultData {
  productionId: number;
}

// BE synchronized
export interface IProductionProcessingCounters {
  bookingsAlreadyInProduceCount?: number;
  totalBookingCount?: number;
  joinedRideCount?: number;
  joinedBookingCount?: number;
  producedRideCount?: number;
  producedBookingCount?: number;
  failedToSaveProducedRidesCount?: number;
  rideTooShortBookingCount?: number;
  notFoundRidesToJoinBookingCount?: number;
  failedToSaveJoinedRideCount?: number;
  failedToSaveJoinedBookingCount?: number;
  joinedSentRideSupplierCanceledCount?: number;
  joinedSentRideSupplierUpdatedCount?: number;
  courseJoinedSentRideSupplierCanceledCount?: number;
  courseJoinedSentRideSupplierUpdatedCount?: number;
  coursesSkippedOnNoPassengersCount?: number;
  coursesAlreadyExistCount?: number;
  producedCoursesCount?: number;
  notSavedCoursesCount?: number;
  skippedPassengerBookingCount?: number;
}

export interface IProductionStatistics extends IProductionProcessingCounters {
  allInputBookingIds?: number[];
  producedBookingIds?: number[];
  uniqueProducedBookingIds?: number[];
  uniqueJoinedBookingIds?: number[];
  bookingsAlreadyInProduceIds?: number[];
  joinedBookingIds?: number[];
  joinedRideIds?: number[];
  joinedSentRideSupplierCanceledIds?: number[];
  joinedSentRideSupplierUpdatedIds?: number[];
  courseJoinedSentRideSupplierCanceledIds?: number[];
  courseJoinedSentRideSupplierUpdatedIds?: number[];
  producedSavedBookingIds?: number[];
  producedSavedRideIds?: number[];
  failedToSaveJoinedBookingIds?: number[];
  failedToSaveJoinedRideIds?: number[];
  rideTooShortBookingIds?: number[];
  notFoundRidesToJoinBookingIds?: number[];
  producedAndJoinedRideIds?: number[];
  coursesSkippedOnNoPassengers?: ICourseScheduleMetricItem[];
  coursesAlreadyExist?: ICourseScheduleMetricItem[];
  producedCourses?: ICourseScheduleMetricItem[];
  notSavedCourses?: ICourseScheduleMetricItem[];
  skippedPassengerBookingIds?: number[];
  producedAndJoinedRideFirstDateISO?: TDateISO;
  producedAndJoinedRideEndDateISO?: TDateISO;
  hubBookingIds?: number[];
  hubBookingCount?: number;
  nonHubBookingCount?: number;
  hubMainRideIds?: number[];
  hubMainRideCount?: number;
  hubPersonalRideIds?: number[];
  hubPersonalRideCount?: number;
  hubExpectedMainRideCount?: number;
  hubExpectedPersonalRideCount?: number;
  hubBookingsSkippedInSplitIds?: number[];
  hubBookingsSkippedInSplitCount?: number;
  hubPersonalGeoFailedBookingIds?: number[];
  hubPersonalGeoFailedCount?: number;
  hubBookingsWithMainSegmentIds?: number[];
  hubBookingsWithMainSegmentCount?: number;
  hubIncompletePairingBookingIds?: number[];
  hubIncompletePairingCount?: number;
  hubBookingsProducedIds?: number[];
  hubBookingsProducedCount?: number;
  hubCompanionBookingIds?: number[];
  hubCompanionBookingCount?: number;
  hubBookingsExcludedFromJoinIds?: number[];
  hubExplicitSkipBookingIds?: number[];
  hubExplicitSkipBookingCount?: number;
  error?: TInfoItem | null;
  debugInfo?: unknown;
}

export interface IProductionStateData extends IProductionStatistics {
  userId?: number;
  action?: BulkProduceTypeEnum;
  current?: number;
  startedAt?: TDateISO;
  finishedAt?: TDateISO;
}

export type TProductionStateLogData = Omit<IProductionStateData, "logData">;

export interface IProductionPollState {
  productionId: number;
  status: ProductionStatusEnum;
  logsCount?: number;
  data: IProductionStateData;
}

export interface IProductionDisplayState extends Omit<
  IProductionStatistics,
  "logData"
> {
  isLoading: boolean;
  loadPercentage: number;
  productionId?: number;
  isFinished?: boolean;
  isAborted?: boolean;
}

export interface IProductionPollGroupState {
  productionGroupId: number;
  productionIds: number[];
  states: IProductionPollState[];
  chains: IProductionChainStep[];
  /** The current production id that is being polled */
  productionPollId: number;
  /** The current production id visible in the UI */
  productionUiId: number;
}
